import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  XCircle,
  Play,
  ArrowRight,
  CreditCard,
  Activity,
  Users,
  Calendar,
  Zap,
  Bell,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LowCreditBanner } from '@/components/LowCreditBanner';
import { CreditBalanceWidget } from '@/components/CreditBalanceWidget';
import { ActionCountWidget } from '@/components/ActionCountWidget';
import { TrainingVideoModal } from '@/components/TrainingVideoModal';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { supabase } from '@/integrations/supabase/client';
import { useUnifiedCampaigns } from '@/hooks/useUnifiedCampaigns';
import { useCampaignCallStats } from '@/hooks/useCampaignCallStats';
import { useLocationTrainingVideo } from '@/hooks/useLocationTrainingVideo';

const Dashboard = () => {
  const navigate = useNavigate();
  const { apiKeys, user } = useAuth();
  const [notificationsEnabled, setNotificationsEnabled] = useState<
    boolean | null
  >(null);
  const campaignsResult = useUnifiedCampaigns();

  const campaigns = useMemo(() => campaignsResult.campaigns, [campaignsResult.campaigns]);
  const activeCampaigns = campaigns.filter((c) => c.status === 'active');
  const allCampaignIds = useMemo(() => campaigns.map((c) => c.id), [campaigns]);
  const timerange = useMemo(() => ({ days: 30 }), []);
  const { campaignStats } = useCampaignCallStats(allCampaignIds, timerange);

  const { hasVideo, displayTitle } = useLocationTrainingVideo('dashboard');

  // Check if system is healthy based on API keys
  const isSystemHealthy =
    apiKeys &&
    (apiKeys.vapi ||
      apiKeys.openai ||
      apiKeys.elevenlabs ||
      apiKeys.twilio ||
      apiKeys.twilioSid);

  useEffect(() => {
    fetchNotificationStatus();
  }, [user?.id]);

  const fetchNotificationStatus = async () => {
    if (!user?.id) return;

    try {
      const { data } = await supabase
        .from('user_notification_preferences')
        .select('tier1_enabled')
        .eq('user_id', user.id)
        .maybeSingle();

      setNotificationsEnabled(data?.tier1_enabled ?? true);
    } catch (error) {
      // Default to true if no preferences found
      setNotificationsEnabled(true);
    }
  };

  // Calculate real statistics from campaign data
  const totalConversions = Object.values(campaignStats).reduce(
    (sum, stats) => sum + stats.messages + stats.handRaised,
    0
  );
  const totalCalls = Object.values(campaignStats).reduce(
    (sum, stats) => sum + stats.totalCalls,
    0
  );
  const successRate = totalCalls > 0 ? Math.round((totalConversions / totalCalls) * 100) : 0;

  const stats = {
    activeCampaigns: activeCampaigns.length,
    totalCallsThisMonth: totalCalls,
    conversionsThisMonth: totalConversions,
    successRate,
  };

  return (
    <div className='container mx-auto p-6 space-y-8'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-foreground'>Dashboard</h1>
          <p className='text-muted-foreground'>Your campaign command center</p>
        </div>
        <div className='flex items-center space-x-3'>
          <Button
            variant='outline'
            onClick={() => navigate('/api-settings')}
            className='flex items-center space-x-2'
          >
            {isSystemHealthy ? (
              <>
                <CheckCircle className='h-4 w-4 text-success' />
                <span>API Connected</span>
              </>
            ) : (
              <>
                <XCircle className='h-4 w-4 text-destructive' />
                <span>API Setup</span>
              </>
            )}
          </Button>
          <Button
            variant='outline'
            onClick={() => navigate('/profile?tab=notifications')}
            className='flex items-center space-x-2'
          >
            {notificationsEnabled ? (
              <>
                <CheckCircle className='h-4 w-4 text-success' />
                <span>Notifications On</span>
              </>
            ) : (
              <>
                <XCircle className='h-4 w-4 text-destructive' />
                <span>Notifications Off</span>
              </>
            )}
          </Button>
          {!isSystemHealthy && (
            <p className='text-sm text-muted-foreground'>API Setup Required</p>
          )}
        </div>
      </div>

      {/* Low Credit Warning */}
      <LowCreditBanner />

      {/* Onboarding Checklist — hidden once all steps are done or dismissed */}
      <OnboardingChecklist />

      {/* Main Content Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        {/* Left Column - Essential Widgets */}
        <div className='space-y-6'>
          <ActionCountWidget />
          {/* Campaign Stats with Load Button */}
          <Card>
            <CardHeader>
              <CardTitle>Campaign Overview</CardTitle>
              <CardDescription>Key metrics at a glance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='text-center p-4 bg-muted/50 rounded-lg'>
                      <div className='text-2xl font-bold text-brand-primary'>
                        {stats.activeCampaigns}
                      </div>
                      <div className='text-sm text-muted-foreground'>
                        Active Campaigns
                      </div>
                    </div>
                    <div className='text-center p-4 bg-muted/50 rounded-lg'>
                      <div className='text-2xl font-bold text-brand-secondary'>
                        {stats.totalCallsThisMonth}
                      </div>
                      <div className='text-sm text-muted-foreground'>
                        Calls This Month
                      </div>
                    </div>
                    <div className='text-center p-4 bg-muted/50 rounded-lg'>
                      <div className='text-2xl font-bold text-brand-accent'>
                        {stats.conversionsThisMonth}
                      </div>
                      <div className='text-sm text-muted-foreground'>
                        Conversions This Month
                      </div>
                    </div>
                    <div className='text-center p-4 bg-muted/50 rounded-lg'>
                      <div className='text-2xl font-bold text-success'>
                        {`${stats.successRate}%`}
                      </div>
                      <div className='text-sm text-muted-foreground'>
                        Success Rate This Month
                      </div>
                    </div>
                  </div>

                  {stats.activeCampaigns === 0 && (
                    <div className='mt-4 p-4 bg-accent/50 rounded-lg text-center'>
                      <p className='text-sm text-muted-foreground mb-3'>
                        Ready to launch your first campaign?
                      </p>
                      <Button
                        variant='default'
                        onClick={() => navigate('/campaigns')}
                        className='w-full'
                      >
                        <ArrowRight className='h-4 w-4 mr-2' />
                        Create Campaign
                      </Button>
                    </div>
                  )}
                  {/* CTA Button */}
                  <Button 
                    onClick={() => navigate('/actions')}
                    className="w-full transition-all duration-200 hover:shadow-md"
                    size="sm"
                  >
                    Visit Action Center
                  </Button>
                </div>
            </CardContent>
          </Card>
          <CreditBalanceWidget />
        </div>

        {/* Right Column - Quick Actions & Progress */}
        <div className='space-y-6'>
          {/* Get Started Section */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Play className='h-5 w-5' />
                Quick Start
              </CardTitle>
              <CardDescription>
                New to the platform? Watch our getting started guide.
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              {hasVideo && (
                <TrainingVideoModal
                  trigger={
                    <Button className='w-full' size='lg'>
                      <Play className='h-4 w-4 mr-2' />
                      {displayTitle || 'Watch Getting Started Video'}
                    </Button>
                  }
                  locationKey='dashboard'
                />
              )}

              {!isSystemHealthy ? (
                <Button
                  variant='outline'
                  className='w-full'
                  onClick={() => navigate('/api-settings')}
                >
                  <Zap className='h-4 w-4 mr-2' />
                  Setup API Keys First
                </Button>
              ) : (
                <div className='space-y-2'>
                  <Button
                    variant='outline'
                    className='w-full'
                    onClick={() => navigate('/script-management')}
                  >
                    Create Your First Script
                  </Button>
                  <Button
                    variant='outline'
                    className='w-full'
                    onClick={() => navigate('/prospects')}
                  >
                    Add Prospect List
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
